// src/components/PredictionsView.tsx

import React, { useCallback } from 'react'
import { useAppDispatch } from '../../store/hooks'
import {
  setSelectedContentId,
  setEditingContent,
  clearAllBoundingBoxes,
} from '../../store/slices/annotationSlice'
import { Content } from '../../types/models/content'
import ContentImage from '../tools/ContentImage'
import { Bot } from 'lucide-react'
import GalleryScaffold from '../gallery/Scaffold'
import GalleryGrid from '../gallery/Grid'
import GalleryTile from '../gallery/Tile'

const AiBadge = () => (
  <span className="badge bg-primary-subtle text-primary-emphasis d-inline-flex align-items-center">
    <Bot size={14} className="me-1" /> AI
  </span>
)

interface AnnotationsGalleryViewProps {
  content: Content[]
  isContentLoading: boolean
  onContentSelect?: (content: Content) => void
  imagesPerRow: number
  selectedContentIds?: Set<string>
  selectable?: boolean
}

const AnnotationsGalleryView: React.FC<AnnotationsGalleryViewProps> = ({
  content,
  isContentLoading,
  onContentSelect,
  imagesPerRow,
  selectedContentIds,
  selectable = false,
}) => {
  const dispatch = useAppDispatch()

  const handleImageClick = useCallback(
    (content: Content) => {
      if (selectable && onContentSelect) {
        // In selectable mode, clicking toggles selection
        onContentSelect(content)
      } else {
        // In non-selectable mode, clicking opens the image
        dispatch(setSelectedContentId(content.id))
        dispatch(setEditingContent(content))
        dispatch(clearAllBoundingBoxes())
        if (onContentSelect) {
          onContentSelect(content)
        }
      }
    },
    [dispatch, onContentSelect, selectable]
  )

  return (
    <GalleryScaffold
      isLoading={isContentLoading}
      empty={!content?.length}
      emptyMessage="No content available."
    >
      <GalleryGrid imagesPerRow={imagesPerRow}>
        {content.map((c: Content) => (
          <GalleryTile key={c.id} badge={c.annotation?.[0]?.ai_labeled ? <AiBadge /> : undefined}>
            <ContentImage
              content={c}
              onImageClick={handleImageClick}
              selectable={selectable}
              selected={selectedContentIds?.has(c.id) || false}
            />
          </GalleryTile>
        ))}
      </GalleryGrid>
    </GalleryScaffold>
  )
}

export default AnnotationsGalleryView
